import { Link } from 'react-router';
import type { Route } from './+types/advertise';

export function meta(_: Route.MetaArgs) {
  return [
    { title: 'Advertise — Tampa Bay Networking Calendar' },
    {
      name: 'description',
      content:
        'Sponsor the Tampa Bay Networking Calendar. One tasteful sponsor slot, seen by the people planning their week across the Bay. $25/week or $60/month, four sponsors at a time.',
    },
    { property: 'og:image', content: '/og-default.png' },
  ];
}

export default function Advertise(_: Route.ComponentProps) {
  return (
    <main>
      {/* ── Hero — flowing vector line-work on ink ── */}
      <section className="adv-hero">
        <FlowField />
        <div className="container">
          <div className="adv-hero-inner">
            <p className="eyebrow adv-eyebrow">Sponsorship</p>
            <h1 className="adv-title">
              Put your name where
              <br />
              the Bay is <span className="accent">already looking</span>.
            </h1>
            <p className="adv-lede">
              One sponsor slot on the calendar the region checks to plan its week. Not a banner farm
              — a single, tasteful placement, held by four sponsors at a time.
            </p>
            <div className="adv-hero-actions">
              <Link to="/contact?subject=Advertising" className="ui-btn btn-primary btn-lg">
                Inquire about sponsoring
              </Link>
              <a href="#rates" className="ui-btn btn-secondary on-dark btn-lg">
                See the rates
              </a>
            </div>
          </div>
        </div>
        <div className="wave" aria-hidden="true">
          <svg viewBox="0 0 1440 80" preserveAspectRatio="none" focusable="false">
            <path
              d="M0,44 C240,84 480,80 720,50 C960,20 1200,18 1440,50 L1440,80 L0,80 Z"
              fill="var(--paper)"
            />
          </svg>
        </div>
      </section>

      {/* ── What it is — three honest points with line-art ── */}
      <section className="band band--paper">
        <div className="container">
          <div className="section-head">
            <div>
              <span className="eyebrow">The placement</span>
              <h2 className="section-title">Simple, and in the right room</h2>
            </div>
          </div>

          <div className="adv-points">
            <article className="adv-point">
              <IconAudience />
              <h3>An intentional audience</h3>
              <p>
                Everyone here is deciding where to network next. Your name sits beside the events
                they came to find — not chasing them around the web.
              </p>
            </article>
            <article className="adv-point">
              <IconSlot />
              <h3>One clean slot</h3>
              <p>
                A single sponsor placement on the calendar, matched to the design of the site. No
                pop-ups, no autoplay, nothing that fights the events for attention.
              </p>
            </article>
            <article className="adv-point">
              <IconScarce />
              <h3>Kept scarce on purpose</h3>
              <p>
                Four sponsors at a time, no more. Scarcity keeps every placement legible — and keeps
                the calendar something people trust.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* ── Where it shows — abstract placement mock ── */}
      <section className="band band--gray adv-place">
        <div className="container">
          <div className="adv-place-grid">
            <div>
              <span className="eyebrow">Where it shows</span>
              <h2 className="adv-place-title">Beside the week people plan.</h2>
              <p className="muted adv-place-copy">
                Your sponsor card rides alongside the live calendar — visible on the pages the Bay
                opens to see what's on. Same square, editorial language as the rest of the site, so
                it reads as part of the room, not an interruption.
              </p>
              <p className="adv-place-copy">
                <Link to="/contact?subject=Advertising" className="btn-link">
                  Ask what a placement looks like →
                </Link>
              </p>
            </div>
            <PlacementMock />
          </div>
        </div>
      </section>

      {/* ── Rates ── */}
      <section id="rates" className="band band--paper">
        <div className="container">
          <div className="section-head">
            <div>
              <span className="eyebrow">Rates</span>
              <h2 className="section-title">Plain and honest</h2>
            </div>
            <p className="adv-rate-note muted small">Four sponsors, maximum, at any time.</p>
          </div>

          <div className="adv-rates">
            <article className="adv-rate-card">
              <p className="adv-rate-kicker">By the week</p>
              <p className="adv-rate-num">
                $25<span className="adv-rate-per">/week</span>
              </p>
              <ul className="adv-rate-list">
                <li>One sponsor slot on the calendar</li>
                <li>Runs a full 7 days</li>
                <li>Good for a single event push</li>
              </ul>
              <Link to="/contact?subject=Advertising" className="ui-btn btn-secondary btn-block">
                Inquire — weekly
              </Link>
            </article>

            <article className="adv-rate-card adv-rate-card--feature">
              <p className="adv-rate-kicker">By the month</p>
              <p className="adv-rate-num">
                $60<span className="adv-rate-per">/month</span>
              </p>
              <ul className="adv-rate-list">
                <li>One sponsor slot, held all month</li>
                <li>Best value — under $15 a week</li>
                <li>For steady, ongoing presence</li>
              </ul>
              <Link to="/contact?subject=Advertising" className="ui-btn btn-primary btn-block">
                Inquire — monthly
              </Link>
            </article>
          </div>
        </div>
      </section>

      {/* ── Closing CTA on ink ── */}
      <section className="adv-cta">
        <RingField />
        <div className="container">
          <div className="adv-cta-inner">
            <h2 className="adv-cta-title">
              Four seats. <span className="accent">Reserve one.</span>
            </h2>
            <p className="adv-cta-copy">
              Tell us the event or the brand and we'll send back availability and next steps. A real
              person reads it.
            </p>
            <Link to="/contact?subject=Advertising" className="ui-btn btn-primary btn-lg">
              Inquire about sponsoring
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}

/* ── Inline vector art (self-contained, no remote assets) ──────────────── */

function FlowField() {
  return (
    <svg
      className="adv-flowfield"
      viewBox="0 0 1440 640"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id="advline" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#16a34a" stopOpacity="0.9" />
          <stop offset="1" stopColor="#16a34a" stopOpacity="0.05" />
        </linearGradient>
      </defs>
      <g fill="none" stroke="url(#advline)" strokeWidth="1.5">
        <path d="M-60,120 C300,40 640,300 1040,180 C1300,100 1440,240 1540,180" />
        <path d="M-60,200 C320,120 660,380 1060,260 C1320,180 1460,320 1560,260" />
        <path d="M-60,280 C340,200 680,460 1080,340 C1340,260 1480,400 1580,340" />
        <path d="M-60,360 C360,280 700,540 1100,420 C1360,340 1500,480 1600,420" />
        <path d="M-60,440 C380,360 720,620 1120,500 C1380,420 1520,560 1620,500" />
      </g>
      <g fill="none" stroke="rgba(255,255,255,0.10)" strokeWidth="1">
        <path d="M-60,160 C320,80 640,340 1040,220 C1300,140 1440,280 1540,220" />
        <path d="M-60,320 C360,240 700,500 1100,380 C1360,300 1500,440 1600,380" />
      </g>
    </svg>
  );
}

function RingField() {
  return (
    <svg className="adv-ringfield" viewBox="0 0 600 600" aria-hidden="true" focusable="false">
      <g fill="none" stroke="rgba(22,163,74,0.35)" strokeWidth="1.5">
        <circle cx="480" cy="120" r="60" />
        <circle cx="480" cy="120" r="120" stroke="rgba(22,163,74,0.22)" />
        <circle cx="480" cy="120" r="190" stroke="rgba(255,255,255,0.08)" />
        <circle cx="480" cy="120" r="270" stroke="rgba(255,255,255,0.05)" />
      </g>
    </svg>
  );
}

function IconAudience() {
  return (
    <svg className="adv-ico" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
      <circle cx="24" cy="16" r="7" fill="none" stroke="currentColor" strokeWidth="2" />
      <path
        d="M10 40c0-8 6.3-13 14-13s14 5 14 13"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <circle cx="24" cy="16" r="2.4" fill="var(--accent)" />
    </svg>
  );
}

function IconSlot() {
  return (
    <svg className="adv-ico" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
      <rect x="7" y="12" width="34" height="24" fill="none" stroke="currentColor" strokeWidth="2" />
      <rect x="12" y="18" width="24" height="5" fill="var(--accent)" />
      <path d="M12 28h16M12 32h11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconScarce() {
  return (
    <svg className="adv-ico" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
      <rect x="8" y="20" width="7" height="16" fill="var(--accent)" />
      <rect
        x="18.5"
        y="20"
        width="7"
        height="16"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      />
      <rect x="29" y="20" width="7" height="16" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M8 15h28" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function PlacementMock() {
  return (
    <div className="adv-mock" aria-hidden="true">
      <div className="adv-mock-head">
        <span className="adv-mock-dot" />
        <span className="adv-mock-bar" />
      </div>
      <div className="adv-mock-row">
        <span className="adv-mock-time" />
        <span className="adv-mock-lines">
          <span className="adv-mock-line long" />
          <span className="adv-mock-line" />
        </span>
      </div>
      <div className="adv-mock-sponsor">
        <span className="adv-mock-sponsor-tag">Sponsor</span>
        <span className="adv-mock-line long" />
        <span className="adv-mock-line" />
      </div>
      <div className="adv-mock-row">
        <span className="adv-mock-time" />
        <span className="adv-mock-lines">
          <span className="adv-mock-line long" />
          <span className="adv-mock-line" />
        </span>
      </div>
    </div>
  );
}
